import { useQuery, type QueryClient } from '@tanstack/react-query';

import { fetchAccountStatus, type AccountStatus } from '../auth/account-api';
import { readCachedStatus, writeCachedStatus } from '../auth/account-cache';

export const ACCOUNT_STATUS_QUERY_KEY = ['account_status'] as const;

/** The exact key the hook below registers, so writers can target it. */
export function accountStatusQueryKey(userId: string): readonly unknown[] {
  return [...ACCOUNT_STATUS_QUERY_KEY, userId];
}

/**
 * Publish a status the server already handed us, WITHOUT a refetch.
 *
 * complete_profile() and friends return the full get_account_status() row, so
 * asking for it again is both a wasted round-trip and a failure mode: an
 * invalidate-then-refetch that errors leaves the caller staring at the old
 * status with no way to tell that anything went wrong. Writing the authoritative
 * answer straight into the cache makes the transition synchronous and
 * unfailable — which is what stops a blocking dialog spinning forever.
 */
export function applyAccountStatus(
  queryClient: QueryClient,
  userId: string,
  status: AccountStatus,
): void {
  queryClient.setQueryData(accountStatusQueryKey(userId), status);
  // Keep the localStorage seed in step; setQueryData never runs the queryFn.
  writeCachedStatus(userId, status);
}

export interface AccountStatusState {
  status: AccountStatus | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Everything the app needs to route a signed-in user, in one round-trip.
 *
 * Replaces useCurrentRole (three parallel table reads) + usePhoneVerified (a
 * fourth). Collapsing them is what lets the guards render a single loading
 * state instead of three sequential full-screen ones.
 */
export function useAccountStatus(userId: string | null): AccountStatusState {
  const query = useQuery({
    queryKey: [...ACCOUNT_STATUS_QUERY_KEY, userId],
    enabled: userId !== null,
    staleTime: 30_000,
    // Seeded so a returning user renders instantly; updatedAt 0 marks it stale
    // so the authoritative check still runs on every mount and can demote.
    initialData: userId !== null ? readCachedStatus(userId) : undefined,
    initialDataUpdatedAt: 0,
    queryFn: async (): Promise<AccountStatus> => {
      const status = await fetchAccountStatus();
      // Only SUCCESSFUL answers are persisted. Phase 90's hook wrote a negative
      // on every zero-row read, so one bad read destroyed the cache it depended
      // on and parked the user on /verify-phone for five minutes.
      if (userId !== null) writeCachedStatus(userId, status);
      return status;
    },
  });

  if (userId === null) {
    return { status: null, loading: false, error: null };
  }

  return {
    status: query.data ?? null,
    // Block only on the initial, answer-less load. With a seed in hand we stay
    // rendered while revalidating.
    loading: query.data === undefined && query.isPending,
    // A failed revalidation must not bounce a working user to an error screen,
    // but with no answer at all there is nothing to render, so surface it.
    error: query.data === undefined ? query.error : null,
  };
}
